import { useParams } from 'react-router-dom';
import { publicPath } from 'enums/publicRoutes';
import { useIntl } from 'react-intl';
import { StyledListingSummaryPage } from './style';
import { Button, Spinner, Divider } from '../../../ui';
import { FallbackImage } from 'ui/UploadImage/fallBack/FallBackImage';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { Table, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

interface SummaryQualityMark {
  id: number;
  name: string;
  logo: string;
}

interface SummaryProduct {
  id: number;
  name: string;
  farm_name: string;
  quality_marks: SummaryQualityMark[];
  quantity: number;
  unit: string;
  delivered_quantity: number;
  price?: number;
  total?: number;
}

interface ListingSummary {
  id: number;
  title: string;
  identifier: string;
  transaction_type: string;
  status: string;
  publication_end_date: string;
  contract_start_date: string;
  contract_end_date: string;
  farm_name?: string;
  products: SummaryProduct[];
  summary_url: string;
}

export const ListingSummaryPage = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const intl = useIntl();
  const { user } = useAuth();
  const isAuthenticated = user?.authenticated;
  const [summary, setSummary] = useState<ListingSummary | null>(null);

  const { isLoading, appendData } = useQueryApiClient({
    request: {
      url: 'api/v2/wholesale/listings/:listing/summary',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setSummary(response);
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (listingId) {
      appendData({}, { listing: listingId });
    }
  }, [listingId]);

  const handleCopyLink = async () => {
    if (!summary?.summary_url) return;

    try {
      await navigator.clipboard.writeText(summary.summary_url);
      message.success(intl.formatMessage({ id: 'listing.summary.link_copied' }));
    } catch {
      message.error(intl.formatMessage({ id: 'listing.summary.copy_link_error' }));
    }
  };

  const getColumns = (): ColumnsType<SummaryProduct> => {
    const baseColumns: ColumnsType<SummaryProduct> = [
      {
        title: intl.formatMessage({ id: 'listing.summary.product' }),
        key: 'name',
        render: (_, record) => record.name,
      },
      {
        title: intl.formatMessage({ id: 'listing.summary.farm_name' }),
        key: 'farm_name',
        render: (_, record) => record.farm_name,
      },
      {
        title: intl.formatMessage({ id: 'listing.summary.quality_marks' }),
        key: 'quality_marks',
        render: (_, record) => (
          <div className="quality-marks-cell">
            {record.quality_marks.length > 0
              ? record.quality_marks.map((qmark) => (
                  <Tooltip key={qmark.id} title={qmark.name}>
                    <span>
                      <FallbackImage
                        className="quality-mark"
                        src={qmark.logo || ''}
                        type="notFound"
                        preview={false}
                      />
                    </span>
                  </Tooltip>
                ))
              : '-'}
          </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'listing.summary.quantity' }),
        key: 'quantity',
        render: (_, record) => `${record.quantity} ${record.unit}`,
      },
      {
        title: intl.formatMessage({ id: 'listing.summary.delivered' }),
        key: 'delivered_quantity',
        render: (_, record) => `${record.delivered_quantity} ${record.unit}`,
      },
    ];

    if (isAuthenticated) {
      baseColumns.push(
        {
          title: intl.formatMessage({ id: 'listing.summary.price' }),
          key: 'price',
          render: (_, record) =>
            record.price !== undefined ? `${record.price.toFixed(2)} EUR` : '-',
        },
        {
          title: intl.formatMessage({ id: 'listing.summary.total' }),
          key: 'total',
          render: (_, record) =>
            record.total !== undefined ? `${record.total.toFixed(2)} EUR` : '-',
        }
      );
    }

    return baseColumns;
  };

  if (!summary && isLoading) {
    return (
      <StyledListingSummaryPage>
        <Spinner spinning={true}>
          <div className="summary-container" />
        </Spinner>
      </StyledListingSummaryPage>
    );
  }

  if (!summary) {
    return (
      <StyledListingSummaryPage>
        <div className="summary-container">
          {intl.formatMessage({ id: 'listing.not_found' })}
        </div>
      </StyledListingSummaryPage>
    );
  }

  const formatDate = (date: string | null) => {
    return date ? dayjs(date).format('DD.MM.YYYY') : '-';
  };

  const getContractPeriod = () => {
    if (summary.contract_start_date && summary.contract_end_date) {
      return `${formatDate(summary.contract_start_date)} - ${formatDate(summary.contract_end_date)}`;
    }
    return '-';
  };

  return (
    <StyledListingSummaryPage>
      <div className="summary-container">
        <div className="summary-header">
          <div className="header-row">
            <div className="title-section">
              <h1>{summary.title}</h1>
              <span className="identifier">{summary.identifier}</span>
            </div>
            <div className="actions-section">
              {isAuthenticated && (
                <Button
                  label={intl.formatMessage({ id: 'listing.summary.copy_link' })}
                  onClick={handleCopyLink}
                  type="default"
                />
              )}
            </div>
          </div>
        </div>
        <Divider />
        <div className="info-section">
          <div className="info-item farm-name-item">
            <div className="info-label">
              {intl.formatMessage({ id: 'listing.farm_name' })}
            </div>
            <div className="info-value">
              {summary.farm_name || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">
              {intl.formatMessage({ id: 'listing.transaction_type_label' })}
            </div>
            <div className="info-value">
              {intl.formatMessage({
                id: `listing.transaction_type.public.${summary.transaction_type}`,
              })}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">
              {intl.formatMessage({ id: 'listing.summary.closing_date' })}
            </div>
            <div className="info-value">
              {formatDate(summary.publication_end_date)}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">
              {intl.formatMessage({ id: 'listing.summary.contract_term' })}
            </div>
            <div className="info-value">{getContractPeriod()}</div>
          </div>
          <div className="info-item listing-link-item">
            <div className="info-label">
              {intl.formatMessage({ id: 'listing.summary.listing_link' })}
            </div>
            <div className="info-value">
              <a
                href={publicPath.wholesaleListingDetail(summary.id)}
                className="listing-link"
              >
                {`${window.location.origin}${publicPath.wholesaleListingDetail(
                  summary.id
                )}`}
              </a>
            </div>
          </div>
        </div>

        <div className="products-section">
          <h2>{intl.formatMessage({ id: 'listing.products' })}</h2>
          <Table
            dataSource={summary.products}
            columns={getColumns()}
            pagination={false}
            rowClassName={(record) =>
              record.delivered_quantity >= record.quantity ? 'fully-delivered' : ''
            }
          />
        </div>

      </div>
    </StyledListingSummaryPage>
  );
};
